import type { ReactNode } from "react";
import type { CurrentResponse, LifetimeGamemode, LifetimeMap } from "@shared/api";

export function LifetimeBreakdowns({ current }: { current: CurrentResponse }) {
  const breakdowns = current.breakdowns ?? { gamemodes: [], maps: [], weapons: [] };
  const modes = breakdowns.gamemodes.slice(0, 6);
  const maps = breakdowns.maps.slice(0, 6);
  const modesHaveExtendedStats = modes.some((mode) => mode.assists > 0 || mode.score > 0);

  return (
    <section className="lifetime-breakdown-section">
      <div className="section-heading">
        <h2>Lifetime breakdowns</h2>
      </div>
      <div className="lifetime-breakdown-grid">
        <BreakdownPanel title="Gamemodes" count={breakdowns.gamemodes.length}>
          {modes.length === 0 ? (
            <div className="breakdown-empty">No gamemode data.</div>
          ) : (
            modes.map((mode) => <GamemodeCard key={mode.id ?? mode.name} mode={mode} showExtended={modesHaveExtendedStats} />)
          )}
        </BreakdownPanel>
        <BreakdownPanel title="Maps" count={breakdowns.maps.length}>
          {maps.length === 0 ? <div className="breakdown-empty">No map data.</div> : maps.map((map) => <MapCard key={map.id ?? map.name} map={map} />)}
        </BreakdownPanel>
      </div>
    </section>
  );
}

function BreakdownPanel({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <div className="breakdown-panel">
      <div className="breakdown-panel-head">
        <h3>{title}</h3>
        <span>{count.toLocaleString()} rows</span>
      </div>
      <div className="breakdown-list">{children}</div>
    </div>
  );
}

function GamemodeCard({ mode, showExtended }: { mode: LifetimeGamemode; showExtended: boolean }) {
  const image = mode.alt_image ?? mode.image;
  return (
    <article className="breakdown-card">
      <Visual className="breakdown-icon-art" image={image} name={mode.name} />
      <div className="breakdown-copy">
        <div className="breakdown-title-row">
          <h4>{mode.name}</h4>
          <span>{mode.matches.toLocaleString()} matches</span>
        </div>
        <div className="breakdown-metrics">
          <BreakdownMetric label="Win" value={formatPercent(mode.win_rate)} />
          <BreakdownMetric label="K/D" value={mode.kd.toFixed(2)} />
          <BreakdownMetric label={showExtended ? "Assists" : "Kills/Match"} value={showExtended ? formatNumber(mode.assists) : formatMaybeDecimal(mode.kills_per_match)} />
          <BreakdownMetric label={showExtended ? "Score" : "Hours"} value={showExtended ? formatNumber(mode.score) : formatHours(mode.hours_played)} />
        </div>
      </div>
    </article>
  );
}

function MapCard({ map }: { map: LifetimeMap }) {
  return (
    <article className="breakdown-card">
      <Visual className="breakdown-map-art" image={map.image} name={map.name} />
      <div className="breakdown-copy">
        <div className="breakdown-title-row">
          <h4>{map.name}</h4>
          <span>{map.matches.toLocaleString()} matches</span>
        </div>
        <div className="breakdown-metrics">
          <BreakdownMetric label="Win" value={formatPercent(map.win_rate)} />
          <BreakdownMetric label="Wins" value={formatNumber(map.wins)} />
          <BreakdownMetric label="Losses" value={formatNumber(map.losses)} />
          <BreakdownMetric label="Hours" value={formatHours(map.hours_played)} />
        </div>
      </div>
    </article>
  );
}

function Visual({ className, image, name }: { className: string; image: string | null; name: string }) {
  return (
    <div className={className} aria-hidden="true">
      {image ? <img src={image} alt="" loading="lazy" onError={(event) => (event.currentTarget.style.display = "none")} /> : <span>{initials(name)}</span>}
    </div>
  );
}

function BreakdownMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="breakdown-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatMaybeDecimal(value: number | null): string {
  return value === null ? "-" : value.toFixed(1);
}

function formatPercent(value: number | null): string {
  return value === null ? "-" : `${(value * 100).toFixed(1)}%`;
}

function formatHours(value: number): string {
  return `${value.toFixed(value >= 10 ? 0 : 1)}h`;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 3)
    .toUpperCase();
}
